import { Component, forwardRef, OnInit, Provider, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ModalService, ToastService, TreeComponent, TreeElement } from 'mbs-ui-kit';
import { ExtendedTreeElement, StepsHelpers } from 'wizards/helpers/steps-helpers';
import { RestoreDisksStepFormValue, RestoreDisksStepValue } from 'wizards/models/restore-disks-models';
import { VirtualDisksSelectedType } from 'wizards/models/select-virtual-disks-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { BaseForRestoreSourceOrDisksStep } from 'wizards/steps/only-restore-steps/base/base-for-restore-source-or-disks-step.directive';

const RestoreDisksStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => RestoreDisksStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-restore-disks-step',
  templateUrl: './restore-disks-step.component.html',
  styleUrls: ['./restore-disks-step.component.scss'],
  providers: [RestoreDisksStepValueAccessor]
})
export class RestoreDisksStepComponent extends BaseForRestoreSourceOrDisksStep<RestoreDisksStepValue> implements OnInit {
  @ViewChild(TreeComponent, { static: false }) treeComponent: TreeComponent;

  public readonly virtualDisksSelectedType = VirtualDisksSelectedType;

  public isDisks = true;
  public disabledTree = false;

  constructor(
    public mainService: RemoteManagementWizardsService,
    public modal: ModalService,
    public stepService: WizardStepsService,
    public toastService: ToastService
  ) {
    super(mainService, modal, stepService, toastService);
  }

  ngOnInit(): void {
    this.initForm();
    this.setValues();

    this.stepService.treeData$.pipe(untilDestroyed(this)).subscribe({
      next: (data: { folders: string[]; treeData: TreeElement[] }) => {
        this.loadDisks(data?.treeData?.filter((machine) => machine.checked || machine.indeterminate) || []);
      }
    });
  }

  initForm(): void {
    this.stepForm = new FormGroup<RestoreDisksStepFormValue>({
      excludes: new FormControl([]),
      folders: new FormControl([], [this.foldersRequiredValidators.bind(this)]),
      type: new FormControl(VirtualDisksSelectedType.Selected)
    });

    this.initFormEvents();
  }

  foldersRequiredValidators(control: AbstractControl): ValidationErrors | null {
    if (control?.value?.length || this.stepForm?.get('type')?.value === VirtualDisksSelectedType.All) return null;

    return { foldersRequired: true };
  }

  updateForm(value: RestoreDisksStepValue): void {
    this.stepForm.reset(value);

    this.stepForm.get('folders').updateValueAndValidity();
  }

  loadDisks(treeElements: TreeElement[]): void {
    this.preparationOfParameters('');

    const excludes = this.stepForm.get('excludes').value;

    this.treeData = treeElements.map((item: ExtendedTreeElement) => {
      const existExc = excludes.some((exc) => !StepsHelpers.pathNotIncludePath(this.isUnix, this.joinForInput, exc, item.path));

      return { ...item, checked: !existExc, indeterminate: existExc, gotChildren: false, hideCheckbox: true };
    });
  }

  virtualDiskRestoreTypeChangeHandler(selectedType: VirtualDisksSelectedType): void {
    this.disabledTree = selectedType === VirtualDisksSelectedType.All;

    this.stepForm?.get('folders')?.updateValueAndValidity();
  }

  changeTreeHandler(event: ExtendedTreeElement): void {
    super.changeTreeHandler(event);

    let existUncheck = false;
    const excludes: string[] = [];

    this.treeComponent.data.forEach((computer: ExtendedTreeElement) => {
      let someDisabled = false;
      const childrenLength = computer.children?.length || 0;
      if (childrenLength > 1) {
        computer.children?.forEach((disk: ExtendedTreeElement) => {
          if (disk.disableCheckbox) someDisabled = true;
          if (!disk.checked) {
            existUncheck = true;
            excludes.push(disk.path);
          }
        });
      }
      if (childrenLength - excludes?.length === 1) {
        computer.children?.forEach((d: ExtendedTreeElement) => d.checked && (d.disableCheckbox = true));
      } else if (someDisabled) computer.children?.forEach((d: ExtendedTreeElement) => d.disableCheckbox && (d.disableCheckbox = false));
    });

    this.stepForm.get('excludes').setValue(excludes);

    if (existUncheck) {
      this.stepForm.get('type').setValue(VirtualDisksSelectedType.Selected);
      this.virtualDiskRestoreTypeChangeHandler(VirtualDisksSelectedType.Selected);
    }
  }

  updateAfterLoadSubtree(treeData: ExtendedTreeElement[]): void {
    const foldersMap = {};
    const excludesMap = {};
    const newFolders: string[] = this.stepForm.get('folders')?.value || [];
    const excludes: string[] = this.stepForm.get('excludes')?.value || [];
    let checkedCount = 0;

    newFolders?.forEach((folder) => (foldersMap[folder] = true));
    excludes?.forEach((folder) => (excludesMap[folder] = true));

    treeData?.forEach((disk: ExtendedTreeElement) => {
      if (disk.checked) {
        if (checkedCount < 2) checkedCount++;
        if (!foldersMap[disk.path]) newFolders.push(disk.path);
      } else if (!excludesMap[disk.path]) excludes.push(disk.path);
    });

    if (checkedCount === 1) treeData?.forEach((disk: ExtendedTreeElement) => disk.checked && (disk.disableCheckbox = true));

    this.stepForm.get('folders').setValue(newFolders);
    this.stepForm.get('excludes').setValue(excludes);
  }
}
